import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, ThemeProvider } from '@mui/material';
import ResearchTheme from '../../styles/Theme.js';
import { event } from '../../services/analyticsService';
import { useAuth } from '../../authentication/AuthContext';

function UseCasesIndexPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Add tracking functions
  const trackUseCaseClick = (useCaseType) => {
    event({
      action: 'use_case_click',
      category: 'navigation',
      label: useCaseType
    });
  };

  const trackExampleClick = (useCaseType) => {
    event({
      action: 'example_click',
      category: 'engagement',
      label: `${useCaseType}_examples`
    });
  };

  const trackGeneralResearchClick = () => {
    event({
      action: 'general_research_click',
      category: 'navigation',
      label: 'from_use_cases_page'
    });

    // Check if user is authenticated
    if (!user) {
      navigate('/login', {
        state: {
          from: '/research',
          message: 'Please log in to access the app and start your research'
        }
      });
      return;
    }

    navigate('/research');
  };

  // Use case data
  const useCases = [
    {
      title: "Startup & Product Validation",
      description: "Test product ideas before building to ensure market fit and identify improvement opportunities",
      icon: "/InstantFlowsicon_wb.png",
      linkPath: "/use-cases/startup-validation",
      examplePath: "/exampleresearches",
      exampleState: { filter: "startup-validation" },
      trackingId: "startup_validation"
    },
    {
      title: "Branding Feedback",
      description: "Understand how your brand identity resonates with different audience segments",
      icon: "/InstantFlowsicon_wb.png",
      linkPath: "/use-cases/branding-feedback",
      examplePath: "/exampleresearches",
      exampleState: { filter: "branding-feedback" },
      trackingId: "branding_feedback"
    },
    {
      title: "Campaign Validation",
      description: "Test marketing campaigns before launch to ensure message clarity and effectiveness",
      icon: "/InstantFlowsicon_wb.png",
      linkPath: "/use-cases/campaign-validation",
      examplePath: "/exampleresearches",
      exampleState: { filter: "campaign-validation" },
      trackingId: "campaign_validation"
    }
  ];

  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box sx={{ textAlign: 'center', pt: 20, pb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 400, mb: 1 }}>
          USE CASES
        </Typography>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
          How InstantFlows Can Help You
        </Typography>
        <Typography variant="h5" component="h3" sx={{ fontWeight: 400, mb: 1 }}>
          Explore our specialized research templates
        </Typography>
        <Typography variant="h5" component="h3" sx={{ fontWeight: 400 }}>
          for different business needs
        </Typography>
      </Box>

      <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 2, fontWeight: '600' }}>
        Our Research Templates
      </Typography>

      <Box sx={{ 
        padding: 4, 
        display: 'flex', 
        flexDirection: 'row',
        gap: 3,
        maxWidth: '1200px',
        margin: '0 auto',
        mb: 3
      }}>
        {useCases.map((useCase, index) => (
          <Paper 
            key={index}
            elevation={3}
            component={Link}
            to={useCase.linkPath}
            onClick={() => trackUseCaseClick(useCase.trackingId)}
            sx={{ 
              padding: 3, 
              borderRadius: 2,
              backgroundColor: index % 2 === 0 ? 'secondary.main' : 'white',
              border: '2px solid',
              borderColor: index === 1 ? 'secondary.main' : 'transparent',
              color: 'text.primary',
              transition: 'transform 0.3s',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-5px)',
                borderColor: 'primary.main'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src={useCase.icon} alt={useCase.title} style={{ height: "60px", width: "60px" }} />
            </Box>
            
            <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 500, textAlign: 'center', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {useCase.title}
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', height: '85px' }}>
              {useCase.description}
            </Typography>
            
            <Box sx={{ 
              mt: 'auto', 
              display: 'flex', 
              justifyContent: 'center'
            }}>
              <Box 
                component={Link} 
                to={useCase.linkPath}
                onClick={() => trackUseCaseClick(useCase.trackingId)}
                sx={{
                  bgcolor: index === 1 ? 'secondary.main' : 'white',
                  color: 'text.primary', 
                  py: 1,
                  px: 3,
                  borderRadius: 1,
                  textDecoration: 'none',
                  fontWeight: 500,
                  textAlign: 'center',
                  width: '60%',
                  '&:hover': {
                    bgcolor: 'primary.light',
                  }
                }}
              >
                Learn More
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      <Box sx={{ maxWidth: '800px', mx: 'auto', px: 3, mb: 6 }}>
        <Typography variant="body1" align="center">
          InstantFlows offers specialized research templates for different business needs. 
          Select the use case that fits your current challenge.
        </Typography>
      </Box>

      <Box sx={{ 
        bgcolor: 'secondary.light', 
        py: 5, 
        px: 3, 
        textAlign: 'center',
        mb: 1
      }}>
        <Typography variant="h4" component="h2" sx={{ mb: 2 }}>
          Not sure which to choose?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Start with our general research template or contact us for guidance.
        </Typography>
        <Box 
          component="button"
          onClick={trackGeneralResearchClick}
          sx={{
            display: 'inline-block',
            bgcolor: 'primary.main',
            color: 'white',
            py: 1.5,
            px: 3,
            borderRadius: 1,
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: '1.1rem',
            border: 'none',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'primary.dark',
            }
          }}
        >
          Start General Research
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default UseCasesIndexPage;